import React, { useState, useEffect, useRef } from 'react';
import EventImage from '../../../components/EventImage';

const CYCLE_INTERVAL = 4000;
const ANIMATION_DURATION = 900;

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {
    return 'TBD';
  }
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

const AnimatedEventCarousel = ({
  topEvents = [],
  loadingEvents,
  nav,
  getBackgroundStyle = () => ({}),
}) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [nextIdx, setNextIdx] = useState(0);
  const [futureIdx, setFutureIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isBackdrop1Animating, setIsBackdrop1Animating] = useState(false);
  const [isNewBackdrop1Visible, setIsNewBackdrop1Visible] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const cycleIntervalRef = useRef(null);
  const animationTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  useEffect(() => {
    if (loadingEvents || topEvents.length === 0) {
      setCurrentIdx(0);
      setNextIdx(0);
      setFutureIdx(0);
      setIsAnimating(false);
      setIsBackdrop1Animating(false);
      setIsNewBackdrop1Visible(false);
      return;
    }

    setCurrentIdx(0);

    if (topEvents.length === 1) {
      setNextIdx(0);
      setFutureIdx(0);
    } else if (topEvents.length === 2) {
      setNextIdx(1);
      setFutureIdx(0);
    } else {
      setNextIdx(1);
      setFutureIdx(2);
    }
  }, [loadingEvents, topEvents]);

  useEffect(() => {
    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
    }

    if (loadingEvents || topEvents.length <= 1) {
      return () => {};
    }

    cycleIntervalRef.current = setInterval(() => {
      setIsAnimating((prev) => {
        if (prev || topEvents.length <= 1) {
          return prev;
        }
        return true;
      });
    }, CYCLE_INTERVAL);

    return () => {
      if (cycleIntervalRef.current) {
        clearInterval(cycleIntervalRef.current);
        cycleIntervalRef.current = null;
      }
    };
  }, [loadingEvents, topEvents.length]);

  useEffect(() => {
    if (!isAnimating) {
      return () => {};
    }

    if (topEvents.length <= 1) {
      setIsAnimating(false);
      return () => {};
    }

    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    animationTimeoutRef.current = setTimeout(() => {
      setIsBackdrop1Animating(true);
      // Show new backdrop 1 after backdrop 1 starts moving
      setTimeout(() => setIsNewBackdrop1Visible(true), 400);
    }, 300);

    resetTimeoutRef.current = setTimeout(() => {
      const total = topEvents.length;
      const newCurrent = nextIdx % total;

      let newNext;
      let newFuture;

      if (total === 1) {
        newNext = newCurrent;
        newFuture = newCurrent;
      } else if (total === 2) {
        newNext = (newCurrent + 1) % total;
        newFuture = newNext;
      } else {
        newNext = (newCurrent + 1) % total;
        newFuture = (newNext + 1) % total;
      }

      setIsResetting(true);
      setIsNewBackdrop1Visible(false);
      setCurrentIdx(newCurrent);
      setNextIdx(newNext);
      setFutureIdx(newFuture);
      setIsAnimating(false);
      setIsBackdrop1Animating(false);
      // Keep no-transition applied briefly to prevent slide-back animation
      setTimeout(() => setIsResetting(false), 50);
    }, ANIMATION_DURATION);

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }
    };
  }, [isAnimating, topEvents.length, nextIdx]);

  useEffect(() => () => {
    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
    }
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  if (loadingEvents || topEvents.length === 0) {
    return null;
  }

  const currentEvent = topEvents[currentIdx] ?? null;
  const nextEvent = topEvents[nextIdx] ?? null;
  const futureEvent = topEvents[futureIdx] ?? null;

  const handleViewEvent = (event) => {
    if (!event?.event_id) return;
    nav(`/customerviewevent/${event.event_id}`);
  };

  const renderCardContent = (event, { isPreview = false } = {}) => (
    <div
      className={`featured-event-inner${isPreview ? ' preview-inner' : ''}`}
      style={{ transform: 'none' }}
    >
      <div className="featured-event-front">
        <div className="featured-event-content">
          <h3 className="featured-event-title">{event?.name ?? 'Stay tuned'}</h3>
        </div>
        <div className="featured-event-image" style={getBackgroundStyle(event?.background)}>
          {event?.file_url ? (
            <EventImage
              image={event.file_url}
              coverimage={event.file_url}
              alt={event?.name ?? 'Featured event'}
              className="featured-event-img"
              loading="eager"
            />
          ) : null}
        </div>
        {Number(event?.user_id) === 46 && (
          <p className="featured-event-disclaimer">
            The events displayed on this website are sample events created for testing purposes only.
          </p>
        )}
      </div>
    </div>
  );

  const backdropClassName = `featured-event-card backdrop${isAnimating ? ' shift-to-middle' : ''}${isResetting ? ' no-transition' : ''}`;
  const previewClassName = `featured-event-card backdrop2 preview-card${
    isAnimating ? ' promote-to-main' : ''
  }${isResetting ? ' no-transition' : ''}`;
  const newBackdrop1ClassName = `featured-event-card new-backdrop1${isNewBackdrop1Visible ? ' fade-in' : ''}`;
  const mainClassName = `featured-event-card main${isAnimating ? ' slide-out' : ''}${isResetting ? ' no-transition' : ''}`;

  const backdropStyle = {
    transform: isAnimating ? 'translate(-10px, -50px)' : 'translate(-45px, -20px)',
    zIndex: isAnimating ? 1 : 0,
  };

  const newBackdrop1Style = {
    transform: 'translate(-45px, -20px)',
    background: '#EFB03424',
    zIndex: -1,
  };

  const previewStyle = {
    transform: isAnimating ? 'translate(25px, -85px)' : 'translate(-10px, -50px)',
  };

  return (
    <div className="hero-right animated-carousel">
      <div className={newBackdrop1ClassName} style={newBackdrop1Style} />

      <div className={backdropClassName} style={backdropStyle} />

      <div className={previewClassName} style={previewStyle}>
        {isAnimating && topEvents.length > 1 && nextEvent ? renderCardContent(nextEvent, { isPreview: true }) : null}
      </div>

      <div className={mainClassName}>{renderCardContent(currentEvent)}</div>
    </div>
  );
};

export default AnimatedEventCarousel;
